import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { useStaticRooms, useAvailableRooms } from '@features/rooms';
import { Routes } from '@common/constants';
import { useAvailabilityCheck } from '@common/hooks/useAvailabilityCheck';
import { toRoomsByAvailability } from './mappers';

export interface UseRoomsResult {
  rooms: Array<{
    id: string;
    title: string;
    imageUrl: string;
    unAvailable: boolean;
    priority?: number | null;
    maxOccupancy: number | null;
    price?:
      | {
          amount: number;
          currencyCode: string;
        }
      | undefined;
    quantity?: number;
    displayUrgencyMessaging?: boolean | undefined;
    maxAdults?: number | null | undefined;
    maxChildren?: number | null | undefined;
    linkRoute: string;
    linkParams: {
      id: string;
      fromDate: string;
      toDate: string;
      adults: number;
      children: number;
    };
  }>;
  isFetching: boolean;
  error: unknown;
  isAvailabilityUnknown: boolean;
  isRetryingAvailability: boolean;
  retryAvailability: () => void;
}

export default function useRooms(
  journeyParams: ParsedJourneyParams,
  language?: string
): UseRoomsResult {
  const {
    fromDate: jpFrom,
    toDate: jpTo,
    adults,
    children,
    childAges,
    rooms: jpRooms,
  } = journeyParams;

  const fromDate = jpFrom.format('YYYY-MM-DD');
  const toDate = jpTo?.format('YYYY-MM-DD') || jpFrom.add(1, 'days').format('YYYY-MM-DD');

  const staticRoomsQuery = useStaticRooms(language);
  const availableRoomsQuery = useAvailableRooms({
    fromDate,
    toDate,
    adults,
    children,
    childAges,
    rooms: jpRooms,
  });
  const staticRooms = staticRoomsQuery.data?.data ?? [];
  const availableRooms = availableRoomsQuery.data?.data ?? [];
  const roomList = toRoomsByAvailability(staticRooms, availableRooms, jpRooms.length);

  const rooms = roomList.map((room) => ({
    ...room,
    linkRoute: Routes.ROOM_DETAIL,
    linkParams: {
      id: room.id,
      fromDate,
      toDate,
      adults,
      children,
    },
  }));

  const { isAvailabilityUnknown, isRetrying } = useAvailabilityCheck(availableRoomsQuery);

  return {
    rooms,
    isFetching: staticRoomsQuery.isFetching || availableRoomsQuery.isFetching,
    error: staticRoomsQuery.error || availableRoomsQuery.error,
    isAvailabilityUnknown,
    isRetryingAvailability: isRetrying,
    retryAvailability: () => void availableRoomsQuery.refetch(),
  };
}
